/**
 * Identité de l'élève — date de naissance et genre (Sprint 21, inscription).
 *
 * UN SEUL contrat pour les DEUX parcours d'inscription :
 *  - auto-inscription d'un élève de 14 ans ou plus
 *    (`POST /v1/auth/register-eleve-autonome`) ;
 *  - ajout d'un enfant par le parent (`POST /v1/foyer/enfants`) et par la
 *    console d'administration (`POST /v1/admin/enfants`).
 *
 * Mêmes noms de champs, mêmes valeurs acceptées, mêmes bornes, mêmes
 * fonctions de validation — c'est le point : la campagne de test
 * (`docs/tests/`) a montré que ce qui casse, casse entre deux écrans qui
 * croient parler le même langage.
 *
 * ⚠️ Ces fonctions sont PURES et sans dépendance : elles sont appelées par
 * l'API (zod), par l'app mobile (activation du bouton d'envoi, bornes du
 * sélecteur) et par le tableau de bord web. Aucune duplication de règle.
 *
 * Protection des renseignements personnels : la date de naissance d'un mineur
 * est un identifiant indirect fort (registre 03, PRIV-07 « minimisation »,
 * SEC-07 « chiffrement au niveau champ »). Elle est stockée CHIFFRÉE, comme
 * `age`, `ville` et `ecole`, et n'est renvoyée que par les routes du foyer du
 * parent titulaire (`/v1/foyer/enfants`) — jamais par les routes tuteur, les
 * agrégats de suivi ou la télémétrie.
 */
/**
 * Genre déclaré de l'élève.
 *
 * `prefere_ne_pas_repondre` est une VALEUR à part entière, distincte de
 * l'absence de réponse (`null`) : refuser de répondre est un choix, pas un
 * champ vide. Le stockage doit pouvoir distinguer les deux — c'est ce qui
 * permet, plus tard, de ne plus jamais reposer la question à qui a déjà
 * refusé d'y répondre.
 */
export const GENRES = ['fille', 'garcon', 'autre', 'prefere_ne_pas_repondre'];
/** Libellés d'interface (français, registre neutre) — partagés mobile + web. */
export const GENRE_LIBELLE = {
    fille: 'Fille',
    garcon: 'Garçon',
    autre: 'Autre',
    prefere_ne_pas_repondre: 'Préfère ne pas répondre',
};
/**
 * Fenêtre d'âge plausible pour un élève de la plateforme.
 *
 * Ces deux bornes existaient déjà côté API (`household.service.assertAge`,
 * 4–25) ; elles sont remontées ici pour que le sélecteur de date des clients
 * et la validation du serveur ne puissent plus diverger. Le secondaire
 * québécois couvre ~12–17 ans : la fenêtre est volontairement plus large
 * (reprise d'études, avance/retard scolaire, fratrie inscrite tôt) — elle sert
 * à écarter l'absurde (date future, 1900), pas à trier les élèves.
 */
export const AGE_ELEVE_MIN = 4;
export const AGE_ELEVE_MAX = 25;
/**
 * Âge à partir duquel un mineur peut consentir seul au traitement de ses
 * renseignements personnels et donc s'inscrire sans parent (Loi 25 + art. 14
 * C.c.Q. ; registre 03, R2 et PRIV-04).
 */
export const AGE_MINIMUM_ELEVE_AUTONOME = 14;
/** Format d'une date civile : `AAAA-MM-JJ`. */
const DATE_CIVILE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * Analyse une date civile `AAAA-MM-JJ` STRICTEMENT.
 *
 * `new Date('2011-02-31')` ne lève pas : il glisse au 3 mars. On recompose donc
 * la chaîne après coup et on la compare à l'entrée — un 31 février est refusé.
 * Le fuseau est neutralisé (`Date.UTC`) : une date de naissance est une date
 * civile, pas un instant.
 */
export function analyserDateCivile(valeur) {
    const m = DATE_CIVILE_RE.exec(valeur.trim());
    if (!m)
        return null;
    const annee = Number(m[1]);
    const mois = Number(m[2]);
    const jour = Number(m[3]);
    if (mois < 1 || mois > 12 || jour < 1 || jour > 31)
        return null;
    const d = new Date(Date.UTC(annee, mois - 1, jour));
    if (d.getUTCFullYear() !== annee || d.getUTCMonth() !== mois - 1 || d.getUTCDate() !== jour) {
        return null;
    }
    return d;
}
/** Formate une date en `AAAA-MM-JJ` (composantes LOCALES : c'est une date civile). */
export function versDateCivile(d) {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const jj = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${jj}`;
}
/**
 * Âge révolu à la date de référence. `null` si la date n'est pas analysable.
 *
 * Implémentation unique du calcul d'âge du produit : l'API la réutilise (elle
 * portait sa propre copie dans `register.service.ts`), les clients aussi.
 */
export function ageRevolu(dateNaissance, ref = new Date()) {
    const d = analyserDateCivile(dateNaissance);
    if (!d)
        return null;
    let age = ref.getFullYear() - d.getUTCFullYear();
    const moisEcart = ref.getMonth() - d.getUTCMonth();
    if (moisEcart < 0 || (moisEcart === 0 && ref.getDate() < d.getUTCDate()))
        age -= 1;
    return age;
}
/**
 * Bornes acceptables d'un sélecteur de date de naissance, à la date de
 * référence. `min` = la plus ancienne, `max` = la plus récente (jamais dans le
 * futur). Les deux clients configurent leur sélecteur avec CES valeurs.
 */
export function bornesDateNaissance(ref = new Date()) {
    const min = new Date(ref.getFullYear() - AGE_ELEVE_MAX, ref.getMonth(), ref.getDate());
    const max = new Date(ref.getFullYear() - AGE_ELEVE_MIN, ref.getMonth(), ref.getDate());
    return { min: versDateCivile(min), max: versDateCivile(max) };
}
/**
 * Date par défaut proposée à l'ouverture du sélecteur : un élève au milieu du
 * secondaire québécois (14 ans). Ouvrir sur « aujourd'hui » obligerait à faire
 * défiler quatorze années à chaque inscription.
 */
export function dateNaissanceParDefaut(ref = new Date()) {
    return versDateCivile(new Date(ref.getFullYear() - AGE_MINIMUM_ELEVE_AUTONOME, ref.getMonth(), ref.getDate()));
}
/**
 * Valide une date de naissance. `null` = valide.
 *
 * Règle unique, partagée par le serveur (zod + service) et par les clients
 * (activation du bouton d'envoi). Une date future est distinguée d'une date
 * simplement hors bornes : le message d'erreur n'est pas le même.
 */
export function refuserDateNaissance(valeur, ref = new Date()) {
    const d = analyserDateCivile(valeur);
    if (!d)
        return 'format';
    const age = ageRevolu(valeur, ref);
    if (age === null)
        return 'format';
    if (d.getTime() > Date.UTC(ref.getFullYear(), ref.getMonth(), ref.getDate()))
        return 'futur';
    if (age < AGE_ELEVE_MIN || age > AGE_ELEVE_MAX)
        return 'hors_bornes';
    return null;
}
/** Raccourci booléen de `refuserDateNaissance`. */
export function dateNaissanceValide(valeur, ref = new Date()) {
    return refuserDateNaissance(valeur, ref) === null;
}
/** L'élève peut-il s'inscrire seul (14 ans ou plus) à la date de référence ? */
export function peutSInscrireSeul(dateNaissance, ref = new Date()) {
    const age = ageRevolu(dateNaissance, ref);
    return age !== null && age >= AGE_MINIMUM_ELEVE_AUTONOME;
}
/* ─── Le parent ou tuteur d'un élève autonome — fondateur, 9 octobre 2026 ─── */
/** Longueur maximale du nom d'un parent ou tuteur. */
export const NOM_REPONDANT_MAX = 120;
/** La phrase dite à l'élève pour chaque refus — la MÊME au serveur et à l'écran. */
export const MESSAGES_REFUS_REPONDANT = {
    nom_requis: 'Le nom d’un parent ou tuteur est requis.',
    courriel_invalide: 'Le courriel du parent ou tuteur n’est pas valide.',
    courriel_de_l_eleve: 'Le courriel du parent ou tuteur doit être différent du tien : c’est lui qu’on doit pouvoir joindre.',
};
const FORME_COURRIEL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
/**
 * Valide le parent ou tuteur qu'un élève de 14 ans et plus déclare : un nom,
 * un courriel bien formé, et qui n'est PAS celui de l'élève — sans quoi « un
 * parent doit pouvoir être joint » ne voudrait rien dire. Une seule fonction
 * pour le serveur (`register-eleve-autonome`, `PUT /billing/repondant`) et pour
 * les deux formulaires du client. `null` : rien à redire.
 */
export function refuserRepondant(nom, courriel, courrielEleve) {
    const n = nom.trim();
    if (n.length === 0 || n.length > NOM_REPONDANT_MAX)
        return 'nom_requis';
    const c = courriel.trim().toLowerCase();
    if (c.length > 254 || !FORME_COURRIEL.test(c))
        return 'courriel_invalide';
    if (c === courrielEleve.trim().toLowerCase())
        return 'courriel_de_l_eleve';
    return null;
}
//# sourceMappingURL=eleve-identite.js.map